import { useEffect, useState } from "react";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import Navbar from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import {
  ChevronLeft,
  ChevronRight,
  Download,
  BookOpen,
  Share2,
  Copy,
  MessageCircle,
  Facebook,
  Twitter,
} from "lucide-react";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";

export default function ComicReaderPage() {
  const { id } = useParams();
  const [comic, setComic] = useState(null);
  const [page, setPage] = useState(0);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareForm, setShareForm] = useState({
    recipient_name: "",
    message: "",
  });
  const [shareUrl, setShareUrl] = useState("");

  const load = async () => {
    const r = await api.get(`/comics/${id}`);
    setComic(r.data);
  };

  useEffect(() => {
    load();
    const t = setInterval(async () => {
      try {
        const r = await api.get(`/comics/${id}/status`);
        if (r.data.status === "ready") {
          await load();
          clearInterval(t);
        } else if (r.data.status === "generating") {
          await load();
        }
      } catch (e) {
        console.error("Comic status poll failed:", e);
      }
    }, 3000);
    return () => clearInterval(t);
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!comic)
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="p-12 text-center font-body font-bold">Loading...</div>
      </div>
    );

  const panels = comic.panels || [];
  const columns = comic.panel_layout === "2-column" ? 2 : 1;
  const pageCount = Math.ceil(panels.length / columns);
  const pagePanels = panels.slice(page * columns, page * columns + columns);
  const isGenerating =
    comic.status === "generating" || comic.status === "scripted";
  const progress = panels.length
    ? Math.round((comic.image_progress / panels.length) * 100)
    : 0;

  const buildPanelHTML = (panel, width, height) => {
    // The reader already displays this same URL through a plain <img> (no
    // crossorigin), which the browser caches without CORS metadata. Loading
    // it again here with crossorigin="anonymous" for html2canvas can reuse
    // that cached response and taint the canvas, rendering a blank panel —
    // so bust the cache with a query param to force a fresh CORS fetch.
    const imgSrc = panel.image_url
      ? `${panel.image_url}${panel.image_url.includes("?") ? "&" : "?"}pdf=${Date.now()}`
      : null;
    return `
    <div style="position:relative;width:${width}px;height:${height}px;display:flex;align-items:center;justify-content:center;background:#f4f4f5;overflow:hidden;">
      ${
        imgSrc
          ? `<img src="${imgSrc}" crossorigin="anonymous" style="width:100%;height:100%;object-fit:cover;" />`
          : `<div style="position:absolute;inset:0;background:repeating-linear-gradient(45deg,#e9d5ff 0,#e9d5ff 2px,#fff 0,#fff 50%) 0/10px 10px;display:flex;align-items:center;justify-content:center;">
               <div style="background:#fff;border:3px solid #000;border-radius:16px;padding:24px;box-shadow:4px 4px 0 #000;">
                 <p style="font-size:24px;text-align:center;font-family:sans-serif;">${
                   panel.image_status === "failed"
                     ? "Image unavailable"
                     : "Drawing..."
                 }</p>
               </div>
             </div>`
      }
      ${
        panel.sfx
          ? `<div style="position:absolute;top:24px;right:24px;background:#FBBF24;padding:20px 28px;border:3px solid #000;font-size:28px;font-weight:900;font-family:sans-serif;">${panel.sfx}</div>`
          : ""
      }
      ${
        panel.narration
          ? `<div style="position:absolute;bottom:0;left:0;right:0;border-top:3px solid #000;background:#FEF08A;padding:8px 16px;font-weight:700;font-size:14px;font-family:sans-serif;text-align:center;">${panel.narration}</div>`
          : ""
      }
      ${
        panel.dialogue
          ? `<div style="position:absolute;top:16px;left:50%;transform:translateX(-50%);background:#fff;border:3px solid #000;border-radius:12px;padding:10px 16px;font-size:14px;font-family:${
              comic.font || "sans-serif"
            };max-width:55%;text-align:center;">${panel.dialogue}</div>`
          : ""
      }
    </div>
  `;
  };

  const exportPDF = async () => {
    toast("Generating PDF…", { duration: 2000 });

    // Render width for the off-screen capture (matches max-w-5xl ~896px),
    // split evenly across columns so a 2-column page keeps each panel's 4:3 aspect.
    const captureW = 896;
    const panelW = captureW / columns;
    const captureH = Math.round(panelW * (3 / 4)); // 4:3 per panel

    const pdf = new jsPDF({
      orientation: "landscape",
      unit: "px",
      format: [captureW, captureH],
    });

    for (let pg = 0; pg < pageCount; pg++) {
      if (pg > 0) pdf.addPage([captureW, captureH], "landscape");
      const panelsForPage = panels.slice(pg * columns, pg * columns + columns);

      // Build an off-screen element that mirrors the web page UI exactly
      const container = document.createElement("div");
      container.style.cssText = `
        position: fixed;
        top: -9999px;
        left: -9999px;
        width: ${captureW}px;
        height: ${captureH}px;
        overflow: hidden;
        display: flex;
        background: #f4f4f5;
      `;
      container.innerHTML = panelsForPage
        .map((p) => buildPanelHTML(p, panelW, captureH))
        .join("");

      document.body.appendChild(container);

      // Wait for every image on this page to load
      const imgEls = container.querySelectorAll("img");
      await Promise.all(
        Array.from(imgEls).map(
          (imgEl) =>
            new Promise((res) => {
              if (imgEl.complete) return res();
              imgEl.onload = res;
              imgEl.onerror = res;
            })
        )
      );

      const canvas = await html2canvas(container, {
        useCORS: true,
        allowTaint: false,
        width: captureW,
        height: captureH,
        scale: 1,
        backgroundColor: "#f4f4f5",
      });

      document.body.removeChild(container);

      const imgData = canvas.toDataURL("image/jpeg", 0.92);
      pdf.addImage(imgData, "JPEG", 0, 0, captureW, captureH);
    }

    pdf.save(`${comic.title}.pdf`);
    toast.success("PDF saved!");
  };

  const createShare = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post(`/comics/${id}/share`, shareForm);
      const fullUrl = `${window.location.origin}${r.data.share_url}`;
      setShareUrl(fullUrl);
      toast.success("Share link ready!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Share failed");
    }
  };

  const shareToSocial = (platform) => {
    const caption = `Check out "${comic?.title || "this comic"}" — I made it on ComicVerse!`;
    if (platform === "whatsapp") {
      window.open(
        `https://wa.me/?text=${encodeURIComponent(`${caption} ${shareUrl}`)}`,
        "_blank",
        "noopener,noreferrer"
      );
    } else if (platform === "facebook") {
      window.open(
        `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
        "_blank",
        "noopener,noreferrer"
      );
    } else if (platform === "twitter") {
      window.open(
        `https://twitter.com/intent/tweet?text=${encodeURIComponent(caption)}&url=${encodeURIComponent(shareUrl)}`,
        "_blank",
        "noopener,noreferrer"
      );
    } else if (platform === "native" && navigator.share) {
      navigator
        .share({ title: comic?.title, text: caption, url: shareUrl })
        .catch(() => {});
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1
              className="font-headings uppercase text-4xl"
              data-testid="reader-title"
            >
              {comic.title}
            </h1>
            <p className="font-body font-bold text-black/70">
              {panels.length} panels · {comic.art_style}
            </p>
          </div>
          <div className="flex gap-2">
            <Dialog open={shareOpen} onOpenChange={setShareOpen}>
              <DialogTrigger asChild>
                <Button
                  className="bg-pink-500 hover:bg-pink-400 text-white cv-border cv-shadow-sm cv-press font-headings uppercase rounded-xl"
                  data-testid="reader-share"
                >
                  <Share2 size={18} className="mr-1" />
                  Gift
                </Button>
              </DialogTrigger>
              <DialogContent className="cv-border cv-shadow-lg rounded-2xl bg-white">
                <DialogHeader>
                  <DialogTitle className="font-headings uppercase text-2xl">
                    Gift this comic
                  </DialogTitle>
                </DialogHeader>
                {shareUrl ? (
                  <div className="space-y-3">
                    <p className="font-body font-bold">
                      Send this link to your friend:
                    </p>
                    <div className="flex gap-2">
                      <Input
                        readOnly
                        value={shareUrl}
                        className="cv-border-3 rounded-xl font-body text-sm"
                        data-testid="share-url"
                      />
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(shareUrl);
                          toast.success("Copied!");
                        }}
                        className="cv-border-3 bg-yellow-300 px-3 rounded-lg cv-shadow-sm cv-press"
                        data-testid="copy-share"
                      >
                        <Copy size={16} />
                      </button>
                    </div>
                    <div>
                      <p className="font-body font-bold text-sm mb-2">
                        Or share to:
                      </p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => shareToSocial("whatsapp")}
                          className="cv-border-3 bg-green-400 px-3 py-2 rounded-lg cv-shadow-sm cv-press"
                          data-testid="share-whatsapp"
                          aria-label="Share on WhatsApp"
                        >
                          <MessageCircle size={18} />
                        </button>
                        <button
                          onClick={() => shareToSocial("facebook")}
                          className="cv-border-3 bg-blue-500 text-white px-3 py-2 rounded-lg cv-shadow-sm cv-press"
                          data-testid="share-facebook"
                          aria-label="Share on Facebook"
                        >
                          <Facebook size={18} />
                        </button>
                        <button
                          onClick={() => shareToSocial("twitter")}
                          className="cv-border-3 bg-black text-white px-3 py-2 rounded-lg cv-shadow-sm cv-press"
                          data-testid="share-twitter"
                          aria-label="Share on X"
                        >
                          <Twitter size={18} />
                        </button>
                        {typeof navigator !== "undefined" && navigator.share && (
                          <button
                            onClick={() => shareToSocial("native")}
                            className="cv-border-3 bg-purple-500 text-white px-3 py-2 rounded-lg cv-shadow-sm cv-press"
                            data-testid="share-native"
                            aria-label="Share via device"
                          >
                            <Share2 size={18} />
                          </button>
                        )}
                      </div>
                    </div>
                    <Button
                      onClick={() => {
                        setShareUrl("");
                        setShareOpen(false);
                        setShareForm({ recipient_name: "", message: "" });
                      }}
                      className="bg-purple-600 hover:bg-purple-500 text-white cv-border cv-shadow-sm cv-press font-headings uppercase rounded-xl w-full"
                    >
                      Done
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={createShare} className="space-y-3">
                    <div>
                      <Label className="font-headings uppercase">
                        Friend's name (optional)
                      </Label>
                      <Input
                        className="cv-border-3 mt-1 rounded-xl font-body font-bold"
                        value={shareForm.recipient_name}
                        onChange={(e) =>
                          setShareForm({
                            ...shareForm,
                            recipient_name: e.target.value,
                          })
                        }
                        data-testid="share-name"
                      />
                    </div>
                    <div>
                      <Label className="font-headings uppercase">
                        Message (optional)
                      </Label>
                      <Textarea
                        rows={3}
                        className="cv-border-3 mt-1 rounded-xl font-body font-bold"
                        value={shareForm.message}
                        onChange={(e) =>
                          setShareForm({
                            ...shareForm,
                            message: e.target.value,
                          })
                        }
                        data-testid="share-message"
                        placeholder="Made this just for you!"
                      />
                    </div>
                    <DialogFooter>
                      <Button
                        type="submit"
                        className="bg-yellow-400 hover:bg-yellow-300 text-black cv-border cv-shadow-sm cv-press font-headings uppercase rounded-xl"
                        data-testid="share-submit"
                      >
                        Generate link
                      </Button>
                    </DialogFooter>
                  </form>
                )}
              </DialogContent>
            </Dialog>
            <Button
              onClick={exportPDF}
              className="bg-purple-600 hover:bg-purple-500 text-white cv-border cv-shadow-sm cv-press font-headings uppercase rounded-xl"
              data-testid="reader-download"
            >
              <Download size={18} className="mr-1" />
              Save
            </Button>
            <Link to="/dashboard">
              <Button className="bg-yellow-400 hover:bg-yellow-300 text-black cv-border cv-shadow-sm cv-press font-headings uppercase rounded-xl">
                <BookOpen size={18} className="mr-1" />
                Library
              </Button>
            </Link>
          </div>
        </div>

        {comic.status === "generating" && (
          <div
            className="cv-border cv-shadow rounded-2xl bg-yellow-100 p-6 mb-6"
            data-testid="reader-progress"
          >
            <p className="font-headings uppercase text-xl mb-2">
              Drawing panels... {comic.image_progress}/{panels.length}
            </p>
            <Progress
              value={progress}
              className="h-4 cv-border-3 rounded-full bg-white"
            />
            <p className="font-body font-bold text-sm mt-2 text-black/70">
              Hang tight — illustrating each panel takes a few seconds.
            </p>
          </div>
        )}

        {!pagePanels.length ? (
          <div className="cv-border cv-shadow-lg rounded-2xl bg-white p-12 text-center font-body font-bold">
            No panels yet. Go back to the editor.
          </div>
        ) : (
          <div
            className="cv-border cv-shadow-lg rounded-2xl bg-white overflow-hidden"
            data-testid={`reader-page-${page}`}
          >
            <div
              className={`grid border-b-4 border-black ${
                columns === 2 ? "grid-cols-2" : "grid-cols-1"
              }`}
            >
              {pagePanels.map((panel, i) => (
                <div
                  key={panel.id || i}
                  className={`aspect-[4/3] bg-zinc-100 relative flex items-center justify-center ${
                    columns === 2 && i === 0 ? "border-r-4 border-black" : ""
                  }`}
                  data-testid={`reader-panel-${page * columns + i}`}
                >
                  {panel.image_url ? (
                    <img
                      src={panel.image_url}
                      alt={`Panel ${page * columns + i + 1}`}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="halftone-purple absolute inset-0 flex items-center justify-center">
                      <div className="cv-border bg-white rounded-2xl p-6 cv-shadow-sm">
                        <p className="font-comic text-3xl text-center">
                          {panel.image_status === "failed"
                            ? "Image unavailable"
                            : "Drawing..."}
                        </p>
                      </div>
                    </div>
                  )}
                  {panel.sfx && (
                    <div
                      className="absolute top-6 right-6 burst font-comic text-3xl"
                      style={{
                        background: "#FBBF24",
                        padding: "20px 28px",
                        color: "#000",
                      }}
                    >
                      {panel.sfx}
                    </div>
                  )}
                  {panel.narration && (
                    <div className="absolute bottom-0 left-0 right-0 border-t-4 border-black bg-yellow-200 px-4 py-2 font-body font-bold text-sm text-center">
                      {panel.narration}
                    </div>
                  )}
                  {panel.dialogue && (
                    <div
                      className="absolute top-4 speech-bubble max-w-[60%]"
                      style={{ fontFamily: comic.font }}
                    >
                      {panel.dialogue}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="p-4 flex items-center justify-between">
              <Button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="bg-purple-600 hover:bg-purple-500 text-white cv-border cv-shadow-sm cv-press font-headings uppercase rounded-xl"
                data-testid="reader-prev"
              >
                <ChevronLeft /> Prev
              </Button>
              <span className="font-headings text-xl">
                Page {page + 1} / {pageCount}
              </span>
              <Button
                onClick={() =>
                  setPage((p) => Math.min(pageCount - 1, p + 1))
                }
                disabled={page === pageCount - 1}
                className="bg-purple-600 hover:bg-purple-500 text-white cv-border cv-shadow-sm cv-press font-headings uppercase rounded-xl"
                data-testid="reader-next"
              >
                Next <ChevronRight />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
